import { Group, GroupEmployee, GroupEquipment } from '../../../RaptorJobValidationSchema';
import { CrewField, SelectedEquipment } from '../RaptorJobCreateModal';
import { useFormikContext } from 'formik';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { RaptorJobResourcesFormType } from '../steps/RaptorJobResourcesStep';

interface UseRaptorJobResourcesStepProps {
  selectedEquipment: SelectedEquipment | null;
  setSelectedEquipment: (equipment: SelectedEquipment | null) => void;
  selectedClass: { id?: string | number | null; name?: string | null } | null;
  setSelectedClass: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedType: { id?: string | number | null; name?: string | null } | null;
  setSelectedType: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedSize: { id?: string | number | null; name?: string | null } | null;
  setSelectedSize: (value: { id?: string | number | null; name?: string | null } | null) => void;
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
}
export const useRaptorJobResourcesStep = <T extends RaptorJobResourcesFormType>({
  selectedEquipment,
  setSelectedEquipment,
  selectedClass,
  setSelectedClass,
  selectedType,
  setSelectedType,
  selectedSize,
  setSelectedSize,
  crewFields,
  setCrewFields
}: UseRaptorJobResourcesStepProps) => {
  const { values, setFieldValue } = useFormikContext<T>();
  const { showSnackbar } = useSnackbar();

  const hasEquipment = selectedEquipment !== null;
  const hasCompleteFilters = !!(selectedClass?.id && selectedType?.id && selectedSize?.id);
  const hasTruck = hasEquipment || hasCompleteFilters;
  const hasRole = crewFields.filter((crew) => crew.roleId).length > 0;
  const canAddGroup = hasTruck || hasRole;

  const handleAddCrewField = () => {
    const newField: CrewField = {
      rowId: `crew-${Date.now()}`,
      employeeId: null,
      employeeName: '',
      roleId: null,
      roleName: ''
    };
    setCrewFields([...crewFields, newField]);
  };

  const handleAddGroup = () => {
    const validCrewMembers = crewFields.filter((crew) => crew.roleId);

    const hasEquipment = selectedEquipment !== null;
    const hasCompleteFilters = selectedClass?.id && selectedType?.id && selectedSize?.id;
    const hasTruck = hasEquipment || hasCompleteFilters;
    const hasRole = validCrewMembers.length > 0;

    if (!hasTruck && !hasRole) {
      showSnackbar(
        'Missing Required Information',
        'Please select at least one element: equipment (or filters), or a role to add a group',
        'warning'
      );
      return;
    }

    const employees: GroupEmployee[] = validCrewMembers.map((crew) => ({
      employeeId: crew.employeeId,
      employeeName: crew.employeeName || null,
      roleId: crew.roleId!,
      roleName: crew.roleName || `Role ${crew.roleId}`,
      status: crew.status
    }));

    let equipment: GroupEquipment;

    if (selectedEquipment) {
      equipment = {
        equipmentId: selectedEquipment.id,
        equipmentName: selectedEquipment.name || 'Unknown Equipment',
        equipmentClassId: selectedEquipment.class?.id ?? null,
        equipmentClass: selectedEquipment.class?.name || null,
        equipmentTypeId: selectedEquipment.type?.id ?? null,
        equipmentType: selectedEquipment.type?.name || null,
        equipmentSizeId: selectedEquipment.size?.id ?? null,
        equipmentSize: selectedEquipment.size?.name || null,
        equipmentStatus: selectedEquipment.status?.name ?? null
      };
    } else if (hasCompleteFilters) {
      equipment = {
        equipmentId: 0,
        equipmentName: `${selectedClass!.name} - ${selectedType!.name} - ${selectedSize!.name}`,
        equipmentClassId: Number(selectedClass!.id),
        equipmentClass: selectedClass!.name || null,
        equipmentTypeId: Number(selectedType!.id),
        equipmentType: selectedType!.name || null,
        equipmentSizeId: Number(selectedSize!.id),
        equipmentSize: selectedSize!.name || null,
        equipmentStatus: null
      };
    } else {
      equipment = {
        equipmentId: 0,
        equipmentName: 'No Equipment',
        equipmentClassId: null,
        equipmentClass: null,
        equipmentTypeId: null,
        equipmentType: null,
        equipmentSizeId: null,
        equipmentSize: null,
        equipmentStatus: null
      };
    }

    const newGroup: Group = {
      id: `group-${Date.now()}`,
      employees: employees,
      equipment: equipment
    };

    setFieldValue('groups', [...values.groups, newGroup], false);

    setCrewFields([]);
    setSelectedEquipment(null);
    setSelectedClass(null);
    setSelectedType(null);
    setSelectedSize(null);
  };

  return {
    canAddGroup,
    handleAddCrewField,
    handleAddGroup,
    values
  };
};
